import {createBrowserRouter} from "react-router";
import BusinessOutlined from "@mui/icons-material/BusinessOutlined";
import DashboardOutlined from "@mui/icons-material/DashboardOutlined";
import LocalOfferOutlined from "@mui/icons-material/LocalOfferOutlined";
import {lazy, type ReactNode, Suspense, useEffect, useMemo, useRef} from "react";
import type {Permission} from "../components/rights/RightsSystem.ts";
import {RightsMapper} from "../components/rights/RightsMapper.ts";
import {useAppSelector} from "../hooks/reduxHooks.ts";
import type {UserType} from "../model/user/UserScope.ts";
import {
    CampaignOutlined,
    DescriptionOutlined,
    HelpOutlined,
    HomeOutlined,
    MapsHomeWorkOutlined,
    SettingsOutlined,
    ShoppingCartCheckoutOutlined,
    StorefrontOutlined,
    VignetteOutlined
} from "@mui/icons-material";
import GlobalLoadPage from "../components/layout/GlobalLoadPage.tsx";
import DeletedCustomerPage from "../pages/Crm/Customer/detail/DeletedCustomerPage.tsx";
import RouteErrorFallback from "../components/error/RouteErrorFallback.tsx";

// Lazy-loaded page modules. Each becomes its own JS chunk so the initial
// bundle stays small and pages load on demand.
const DashboardPage = lazy(async () => await import("../pages/Dashboard/DashboardPage.tsx"));
const SupportStartPage = lazy(async () => await import("../pages/SupportStart/SupportStartPage.tsx"));
const GaragePage = lazy(async () => await import("../pages/Garage/GaragePage.tsx"));
const GarageListPage = lazy(async () => await import("../pages/GarageList/GarageListPage.tsx"));
const AdminGarageListPage = lazy(async () => await import("../pages/AdminGarageList/AdminGarageListPage.tsx"));
const AdminGarageGroupListPage = lazy(async () => await import("../pages/AdminGarageGroupList/AdminGarageGroupListPage.tsx"));
const ProductOverviewPage = lazy(async () => await import("../pages/Product/overview/ProductOverviewPage.tsx"));
const UserPage = lazy(async () => await import("../pages/User/UserPage.tsx"));
const ProductDetailPage = lazy(async () => await import("../pages/Product/detail/ProductDetailPage.tsx"));
const ProductCategoryOverviewPage = lazy(async () => await import("../pages/Product/category/ProductCategoryOverviewPage.tsx"));
const EditProductCategoryDetailsPage = lazy(async () => await import("../pages/Product/category/EditProductCategoryDetailsPage.tsx"));
const CreateProductCategoryDetailsPage = lazy(async () => await import("../pages/Product/category/CreateProductCategoryDetailsPage.tsx"));
const CreateGarageProductDetailPage = lazy(async () => await import("../pages/Product/detail/CreateGarageProductDetailPage.tsx"));
const EditGarageProductDetailPage = lazy(async () => await import("../pages/Product/detail/EditGarageProductDetailPage.tsx"));
const DealerProductMassUpdatePage = lazy(async () => await import("../pages/Product/mass-update/DealerProductMassUpdatePage.tsx"));
const NotFoundPage = lazy(async () => await import("../pages/common/NotFoundPage.tsx"));
const OrderOverviewPage = lazy(async () => await import("../pages/Order/overview/OrderOverviewPage.tsx"));
// lazy like the pages: the gate renders BasePage, whose navigation imports this module
const SupportOrderAccessGate = lazy(async () => await import("../components/supportContext/SupportOrderAccessGate.tsx"));
const OrderDetailsPage = lazy(async () => await import("../pages/Order/detail/OrderDetailsPage.tsx"));
const CouponOverviewPage = lazy(async () => await import("../pages/Coupon/overview/CouponOverviewPage.tsx"));
const CouponDetailPage = lazy(async () => await import("../pages/Coupon/detail/CouponDetailPage.tsx"));
const HelpCenterPage = lazy(async () => await import("../pages/HelpCenter/HelpCenterPage.tsx"));
const DocumentsPage = lazy(async () => await import("../pages/HelpCenter/DocumentsPage.tsx"));
const VideosPage = lazy(async () => await import("../pages/HelpCenter/VideosPage.tsx"));
const FaqPage = lazy(async () => await import("../pages/HelpCenter/FaqPage.tsx"));
const EnquiryPage = lazy(async () => await import("../pages/HelpCenter/EnquiryPage.tsx"));
const ContractOverviewPage = lazy(async () => await import("../pages/Contracting/overview/ContractOverviewPage.tsx"));
const ContractPage = lazy(async () => await import("../pages/Contracting/detail/ContractPage.tsx"));
const CustomerDetailPage = lazy(async () => await import("../pages/Crm/Customer/detail/CustomerDetailPage.tsx"));
const GarageUserListPage = lazy(async () => await import("../pages/GarageUserList/GarageUserListPage.tsx"));
const GarageUserDetailPage = lazy(async () => await import("../pages/GarageUserDetail/GarageUserDetailPage.tsx"));
const MailerPage = lazy(async () => await import("../pages/Crm/MailerPage.tsx"));
const EmailTemplatesPage = lazy(async () => await import("../pages/Settings/EmailTemplatesPage.tsx"));
const MailServerSettingsPage = lazy(async () => await import("../pages/Settings/MailServerSettingsPage.tsx"));
const SettingsPage = lazy(async () => await import("../pages/Settings/SettingsPage.tsx"));
const PriceBookOverviewPage = lazy(async () => await import("../pages/PriceBook/overview/PriceBookOverviewPage.tsx"));
const PriceBookEditPage = lazy(async () => await import("../pages/PriceBook/edit/PriceBookEditPage.tsx"));
const SetupFeesEditPage = lazy(async () => await import("../pages/PriceBook/edit/SetupFeesEditPage.tsx"));

const withSuspense = (node: ReactNode): ReactNode => (
    <Suspense fallback={<GlobalLoadPage/>}>
        {node}
    </Suspense>
);

// "Settings": sections shown as tabs below the "Settings" menu entry.
export type RouteTagType = "DPA" | "Menu" | "Settings";

export const SETTINGS_PATH = "/settings";
export const MAIL_SERVER_SETTINGS_PATH = "/settings/mail-server";

export interface Route {
    path: string;
    element: ReactNode;
    tags: readonly RouteTagType[];
    label?: string;
    icon?: ReactNode;
    readPermission?: readonly Permission[];
    /**
     * "all" (default): every readPermission is required.
     * "any": one of them suffices — for umbrella entries such as "Settings"
     * that only bundle sections which carry their own permission checks.
     */
    readPermissionMode?: "all" | "any";
    relevantForUserTypes: readonly UserType[];
    // still reachable, just not offered in the menu (admins reach these via a support context)
    hiddenInMenuFor?: readonly UserType[];
}

// Full route catalog. Defense-in-depth: only routes that match userType AND
// readPermission end up in the browser router for a given user — forbidden
// paths fall through to NotFoundPage.
export const routes: readonly Route[] = [
    {
        path: "/",
        element: <DashboardPage/>,
        tags: ["Menu"],
        label: "defaults.dashboard",
        readPermission: [],
        icon: <DashboardOutlined/>,
        relevantForUserTypes: ["garage", "group"]
    },
    {
        path: "/",
        element: <SupportStartPage/>,
        tags: ["Menu"],
        label: "supportStart.menu",
        readPermission: [],
        icon: <HomeOutlined/>,
        relevantForUserTypes: ["admin"]
    },
    {
        path: "/garage",
        element: <GaragePage/>,
        tags: ["Menu", "DPA"],
        label: "defaults.garage",
        readPermission: ["Garage_read"],
        icon: <MapsHomeWorkOutlined/>,
        relevantForUserTypes: ["garage"]
    },
    {
        path: "/garage",
        element: <GarageListPage/>,
        tags: ["Menu", "DPA"],
        label: "defaults.groupGarages",
        readPermission: ["Garage_list"],
        icon: <MapsHomeWorkOutlined/>,
        relevantForUserTypes: ["group"]
    },
    {
        path: "/admin/garage-list",
        element: <AdminGarageListPage/>,
        tags: ["Menu"],
        label: "defaults.admin.garages",
        readPermission: ["SupportContext_write", "Garage_list"],
        icon: <MapsHomeWorkOutlined/>,
        relevantForUserTypes: ["admin"]
    },
    {
        path: "/admin/garage-groups",
        element: <AdminGarageGroupListPage/>,
        tags: ["Menu"],
        label: "garageGroupList.menu",
        readPermission: ["SupportContext_write", "GarageGroup_list"],
        icon: <BusinessOutlined/>,
        relevantForUserTypes: ["admin"]
    },
    {
        path: "/garage/:garageNo",
        element: <GaragePage/>,
        tags: [],
        label: "defaults.groupGarages",
        readPermission: ["Garage_read", "Garage_list"],
        icon: <BusinessOutlined/>,
        // support opens a garage in its context, see the admin garage list (#56924)
        relevantForUserTypes: ["garage", "group"]
    },
    {
        path: "/product",
        element: <ProductOverviewPage
            type="default"
            navigateProductTo={"product"}
            navigateOwnProductTo={"product/ownProduct"}
        />,
        tags: ["Menu"],
        label: "defaults.products",
        readPermission: ["DealerProduct_list"],
        icon: <StorefrontOutlined/>,
        relevantForUserTypes: ["garage", "group"]
    },
    {
        path: "/admin/product",
        element: <ProductOverviewPage
            type={"admin"} navigateProductTo={"product"}
            navigateOwnProductTo={"product/ownProduct"}/>,
        tags: ["Menu"],
        label: "defaults.admin.products",
        readPermission: [],
        icon: <StorefrontOutlined/>,
        relevantForUserTypes: ["admin"],
        hiddenInMenuFor: ["admin"]
    },
    {
        path: "/product/productCategories",
        element: <ProductCategoryOverviewPage type="default" navigateBackTo={"/product"}/>,
        tags: [],
        label: "defaults.productCategories",
        readPermission: ["ProductCategories_list"],
        icon: <VignetteOutlined/>,
        relevantForUserTypes: ["garage", "group", "admin"]
    },
    {
        path: "/product/productCategories/create",
        element: <CreateProductCategoryDetailsPage navigateBackTo={"/product/productCategories"}/>,
        tags: [],
        label: "defaults.productCategories",
        readPermission: ["ProductCategories_create"],
        icon: <VignetteOutlined/>,
        relevantForUserTypes: ["garage", "group", "admin"]
    },
    {
        path: "/product/productCategory/:productCategoryNo",
        element: <EditProductCategoryDetailsPage navigateBackTo={"/product/productCategories"}/>,
        tags: [],
        label: "defaults.productCategories",
        readPermission: ["ProductCategories_read"],
        icon: <VignetteOutlined/>,
        relevantForUserTypes: ["garage", "group", "admin"]
    },
    {
        path: "/product/:productNo",
        element: <ProductDetailPage navigateBackTo={"/product"}/>,
        tags: [],
        readPermission: ["DealerProduct_read"],
        relevantForUserTypes: ["garage", "group", "admin"]
    },
    {
        path: "/product/massUpdate",
        element: <DealerProductMassUpdatePage navigateBackTo={"/product"}/>,
        tags: [],
        readPermission: ["DealerProduct_MassUpdate_write"],
        relevantForUserTypes: ["garage", "group", "admin"]
    },
    {
        path: "/product/ownProduct/create",
        element: <CreateGarageProductDetailPage navigateBackTo={"/product"}/>,
        tags: [],
        readPermission: ["GarageProduct_create"],
        relevantForUserTypes: ["garage", "group", "admin"]
    },
    {
        path: "/product/ownProduct/:productNo",
        element: <EditGarageProductDetailPage navigateBackTo={"/product"}/>,
        tags: [],
        readPermission: ["GarageProduct_read"],
        relevantForUserTypes: ["garage", "group", "admin"]
    },
    {
        path: "/admin/productCategory",
        element: <ProductCategoryOverviewPage type={"admin"} navigateBackTo={"/admin/product"}/>,
        tags: ["Menu"],
        label: "defaults.admin.productCategories",
        readPermission: [],
        icon: <VignetteOutlined/>,
        relevantForUserTypes: ["admin"],
        hiddenInMenuFor: ["admin"]
    },
    {
        path: "/order",
        // support confirms before every visit that the access is logged (#56952)
        element: <SupportOrderAccessGate><OrderOverviewPage/></SupportOrderAccessGate>,
        tags: ["Menu"],
        label: "defaults.orders",
        readPermission: ["Order_list"],
        icon: <ShoppingCartCheckoutOutlined/>,
        relevantForUserTypes: ["garage", "group", "admin"],
        hiddenInMenuFor: ["admin"]
    },
    {
        path: "/order/:orderNo",
        element: <SupportOrderAccessGate><OrderDetailsPage navigateBackTo={"/order"}/></SupportOrderAccessGate>,
        tags: [],
        readPermission: ["Order_read"],
        relevantForUserTypes: ["garage", "group", "admin"]
    },
    {
        path: "/couponOverview",
        element: <CouponOverviewPage/>,
        tags: ["Menu"],
        label: "defaults.coupons",
        readPermission: ["Coupon_list"],
        icon: <LocalOfferOutlined/>,
        relevantForUserTypes: ["garage", "group", "admin"],
        hiddenInMenuFor: ["admin"]
    },
    {
        path: "/coupon",
        element: <CouponDetailPage navigateBackTo={"/couponOverview"}/>,
        tags: [],
        readPermission: ["Coupon_create"],
        relevantForUserTypes: ["garage", "group", "admin"]
    },
    {
        path: "/coupon/:couponNo",
        element: <CouponDetailPage navigateBackTo={"/couponOverview"}/>,
        tags: [],
        readPermission: ["Coupon_read"],
        relevantForUserTypes: ["garage", "group", "admin"]
    },
    {
        path: "/mailer",
        element: <MailerPage/>,
        tags: ["Menu"],
        readPermission: ["Customer_read", "Promotion_read"],
        label: "defaults.crm.mailer",
        icon: <CampaignOutlined/>,
        relevantForUserTypes: ["garage", "group"]
    },
    {
        path: "/mailer/customer/create",
        element: <CustomerDetailPage navigateBackTo={"/mailer"}/>,
        tags: [],
        readPermission: ["Customer_read", "Customer_create"],
        relevantForUserTypes: ["garage", "group"]
    },
    {
        // linked from mails, keep in sync with order-notification CockpitUrlProvider (#56642)
        path: "/mailer/customer/:customerNo",
        element: <CustomerDetailPage navigateBackTo={"/mailer"}/>,
        tags: [],
        readPermission: ["Customer_read"],
        relevantForUserTypes: ["garage", "group"]
    },
    {
        path: "/mailer/customer/view/:customerNo",
        element: <DeletedCustomerPage navigateBackTo={"/mailer"}/>,
        tags: [],
        readPermission: ["Customer_read"],
        relevantForUserTypes: ["garage", "group"]
    },
    // Settings Menu: one menu entry, the sections below are its tabs (order = tab order).
    {
        path: SETTINGS_PATH,
        element: <SettingsPage/>,
        tags: ["Menu"],
        label: "defaults.settings",
        readPermission: ["MailTemplates_read", "MailServer_read", "User_list"],
        readPermissionMode: "any",
        icon: <SettingsOutlined/>,
        relevantForUserTypes: ["garage", "group"]
    },
    {
        path: "/settings/email-templates",
        element: <EmailTemplatesPage/>,
        tags: ["Settings"],
        label: "defaults.emailTemplates",
        readPermission: ["MailTemplates_read"],
        relevantForUserTypes: ["garage", "group"]
    },
    {
        path: MAIL_SERVER_SETTINGS_PATH,
        element: <MailServerSettingsPage/>,
        tags: ["Settings"],
        label: "defaults.mailServer",
        readPermission: ["MailServer_read"],
        relevantForUserTypes: ["garage", "group"]
    },
    {
        path: "/settings/users",
        element: <GarageUserListPage/>,
        tags: ["Settings"],
        label: "defaults.users",
        readPermission: ["User_list"],
        relevantForUserTypes: ["garage", "group"]
    },
    {
        path: "/settings/users/new",
        element: <GarageUserDetailPage navigateBackTo={"/settings/users"}/>,
        tags: [],
        readPermission: ["User_create"],
        relevantForUserTypes: ["garage", "group"]
    },
    {
        path: "/settings/users/:userNo",
        element: <GarageUserDetailPage navigateBackTo={"/settings/users"}/>,
        tags: [],
        readPermission: ["User_read"],
        relevantForUserTypes: ["garage", "group"]
    },
    {
        path: "/user",
        element: <UserPage/>,
        tags: [],
        label: "defaults.userName",
        readPermission: [],
        relevantForUserTypes: ["garage", "group", "admin"]
    },
    // Admin Menu
    {
        path: "/admin/contract",
        element: <ContractOverviewPage/>,
        tags: ["Menu"],
        label: "defaults.admin.contracts",
        readPermission: ["Contract_read"],
        icon: <DescriptionOutlined/>,
        relevantForUserTypes: ["admin"]
    },
    {
        path: "/admin/contract/:contractId",
        element: <ContractPage/>,
        tags: [],
        readPermission: ["Contract_read"],
        relevantForUserTypes: ["admin"]
    },
    {
        path: "/admin/price-books",
        element: <PriceBookOverviewPage/>,
        tags: [],
        readPermission: ["PriceBook_read"],
        relevantForUserTypes: ["admin"]
    },
    {
        path: "/admin/price-books/setup-fees",
        element: <SetupFeesEditPage/>,
        tags: [],
        readPermission: ["PriceBook_write"],
        relevantForUserTypes: ["admin"]
    },
    {
        path: "/admin/price-books/:bookTypeId",
        element: <PriceBookEditPage/>,
        tags: [],
        readPermission: ["PriceBook_write"],
        relevantForUserTypes: ["admin"]
    },
    // Help Center Menu
    {
        path: "/help",
        element: <HelpCenterPage/>,
        tags: ["Menu"],
        label: "defaults.helpCenter",
        readPermission: [],
        icon: <HelpOutlined/>,
        relevantForUserTypes: ["garage", "group", "admin"]
    },
    {
        path: "/help/documents",
        element: <DocumentsPage/>,
        tags: [],
        label: "defaults.helpCenter.documents",
        readPermission: [],
        relevantForUserTypes: ["garage", "group", "admin"]
    },
    {
        path: "/help/videos",
        element: <VideosPage/>,
        tags: [],
        label: "defaults.helpCenter.videos",
        readPermission: [],
        relevantForUserTypes: ["garage", "group", "admin"]
    },
    {
        path: "/help/faq",
        element: <FaqPage/>,
        tags: [],
        label: "defaults.helpCenter.faq",
        readPermission: [],
        relevantForUserTypes: ["garage", "group", "admin"]
    },
    {
        path: "/help/enquiry",
        element: <EnquiryPage/>,
        tags: [],
        label: "defaults.helpCenter.enquiry",
        readPermission: [],
        relevantForUserTypes: ["garage", "group", "admin"]
    },
    {
        path: "*",
        element: <NotFoundPage/>,
        tags: [],
        label: "404",
        readPermission: [],
        relevantForUserTypes: ["garage", "group", "admin", "unknown"]
    }
];

/**
 * Pure filter: returns only routes the user is allowed to see based on
 * scope (userType) AND granular permissions. Defense-in-depth — forbidden
 * routes never enter the React-Router tree. Testable without React/Redux.
 */
export const getAuthorizedRoutes = (
    allRoutes: readonly Route[],
    userType: UserType,
    permissions: readonly Permission[],
): Route[] =>
    allRoutes.filter(route =>
        route.relevantForUserTypes.includes(userType) &&
        hasReadPermission(route, permissions)
    );

const hasReadPermission = (route: Route, permissions: readonly Permission[]): boolean => {
    const required = route.readPermission ?? [];
    if (required.length === 0) return true;
    return route.readPermissionMode === "any"
        ? required.some(p => permissions.includes(p))
        : required.every(p => permissions.includes(p));
};

/**
 * React hook returning the filtered route list for the current authenticated
 * user. Re-evaluates automatically when auth state changes via useSelector.
 */
export const useAuthorizedRoutes = (): Route[] => {
    const authorities = useAppSelector(state => state.auth.profile?.authorities as string[] | undefined);
    const userType = useAppSelector(state => state.auth.userScope?.type) ?? "unknown";
    // every token refresh brings a new array; only a change of the authorities themselves counts
    const authorityKey = (authorities ?? []).join(",");
    return useMemo(() => {
        const permissions = RightsMapper.getUserPermissions(authorityKey ? authorityKey.split(",") : []);
        return getAuthorizedRoutes(routes, userType, permissions);
    }, [authorityKey, userType]);
};

/** Pure filter: routes carrying the tag; menu entries hidden for the user type are left out. */
export const getRoutesByTag = (
    authorized: readonly Route[],
    tag: RouteTagType,
    userType: UserType,
): Route[] =>
    authorized.filter(route =>
        route.tags.includes(tag) &&
        !(tag === "Menu" && route.hiddenInMenuFor?.includes(userType))
    );

export const useRoutesByTag = (tag: RouteTagType): Route[] => {
    const authorized = useAuthorizedRoutes();
    const userType = useAppSelector(state => state.auth.userScope?.type) ?? "unknown";
    return useMemo(() => getRoutesByTag(authorized, tag, userType), [authorized, tag, userType]);
};

let routerGeneration = 0;

/**
 * Memoized browser router instance built from the user's authorized routes.
 * Replaces the previous useState+setRouter dance in App.tsx.
 *
 * The router is rebuilt when the authorized routes change and for every support
 * context (#56913), so a context starts fresh at the URL of its history entry.
 * RouterProvider keeps the state of the router it was mounted with, so it has to be
 * remounted with the new one: use `key` for that.
 */
export const useRouter = () => {
    const authorized = useAuthorizedRoutes();
    const context = useAppSelector(state => state.auth.supportContext);
    const contextKey = context ? `${context.type}:${context.no}` : "";
    const current = useMemo(
        () => ({
            key: String(++routerGeneration),
            router: createBrowserRouter(authorized.map(route => ({
                path: route.path,
                element: withSuspense(route.element),
                errorElement: <RouteErrorFallback/>,
            }))),
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps -- a new router per context
        [authorized, contextKey],
    );
    // a replaced router would keep listening to the browser history
    const previous = useRef(current);
    useEffect(() => {
        if (previous.current !== current) previous.current.router.dispose();
        previous.current = current;
    }, [current]);
    return current;
};
